// ---------------------------------------------------------------
// Copyright (c) Aspen Publishing. All rights reserved.
// ---------------------------------------------------------------

class EntitlementsCache {
  constructor() {
    this.CACHE_KEY = 'asp_entitlements_cache';
    this.PRODUCTS_CACHE_KEY = 'asp_products_cache';
    this.SESSION_CACHE_KEY = 'asp_session_products_cache';
    this.CACHE_TTL = 24 * 60 * 60 * 1000;
    this.storage = typeof localStorage !== 'undefined' ? localStorage : null;
    this.sessionStorage = typeof sessionStorage !== 'undefined' ? sessionStorage : null;

    this.syncWithServerData();
    this.setupEventListeners();
  }

  setupEventListeners() {
    window.addEventListener('cart:item-added', () => {
      this.invalidateProductValidationCache();
    });
  }

  validateSignature(data) {
    const currentCustomerId = window.theme?.customer?.id;

    if (!currentCustomerId) {
      return false;
    }

    const isValid = Number(currentCustomerId) === Number(data.customerId);

    return isValid;
  }

  syncWithServerData() {
    const serverDataElement = document.getElementById('customer-entitlements-data');

    if (!serverDataElement) {
      console.warn('[EntitlementsCache] ⚠️ No server data element found (#customer-entitlements-data)');

      return;
    }

    try {
      const rawContent = serverDataElement.textContent;
      const serverData = JSON.parse(rawContent);

      const cachedData = this.getCachedData();

      if (!cachedData || cachedData.customerId !== serverData.customerId) {
        this.setCachedData(serverData);

        return;
      }

      const hasContentChanges = this.hasEntitlementsChanged(cachedData.entitlements, serverData.entitlements);

      if (hasContentChanges) {
        const newEntitlements = this.detectNewEntitlements(
          cachedData?.entitlements || [],
          serverData?.entitlements || []
        );

        if (newEntitlements.length > 0) {
          this.invalidateProductValidationCache();
        }

        this.setCachedData(serverData);
      }
    } catch (error) {
      console.error('[EntitlementsCache] ❌ Error syncing with server data:', error);
      console.error('[EntitlementsCache] Error stack:', error.stack);
    }
  }

  getCachedData() {
    try {
      const cachedString = this.storage.getItem(this.CACHE_KEY);

      if (!cachedString) {
        return null;
      }

      const cachedData = JSON.parse(cachedString);

      if (!this.validateSignature(cachedData)) {
        console.warn('[EntitlementsCache] ⚠️ Customer ID mismatch or invalid cache data, clearing cache');
        this.invalidate();

        return null;
      }

      return cachedData;
    } catch (error) {
      console.error('[EntitlementsCache] ❌ Error getting cached data:', error);

      return null;
    }
  }

  setCachedData(data) {
    try {
      const dataString = JSON.stringify(data);
      this.storage.setItem(this.CACHE_KEY, dataString);
    } catch (error) {
      console.error('[EntitlementsCache] ❌ Error setting cached data:', error);
    }
  }

  hasEntitlement(variantId) {
    const cachedData = this.getCachedData();

    if (!cachedData || !cachedData.entitlements) {
      return false;
    }

    const entitlement = cachedData.entitlements.find(
      e => e.variantProductId === variantId || e.variantProductId === String(variantId)
    );

    if (!entitlement) {
      return false;
    }

    const isActive = entitlement.state === 'Active' || entitlement.state === 'Completed';

    return isActive;
  }

  getEntitlements() {
    const cachedData = this.getCachedData();
    const entitlements = cachedData?.entitlements || [];

    return entitlements;
  }

  getActiveEntitlements() {
    const entitlements = this.getEntitlements();
    const activeEntitlements = entitlements.filter(e => e.state === 'Active' || e.state === 'Completed');

    return activeEntitlements;
  }

  hasEntitlementsChanged(oldEntitlements, newEntitlements) {
    if (!Array.isArray(oldEntitlements) || !Array.isArray(newEntitlements)) {
      return true;
    }

    if (oldEntitlements.length !== newEntitlements.length) {
      return true;
    }

    const oldVariantIds = new Set(oldEntitlements.map(e => `${e.variantProductId}-${e.state}-${e.entitlementTypeId}`));

    const newVariantIds = new Set(newEntitlements.map(e => `${e.variantProductId}-${e.state}-${e.entitlementTypeId}`));

    const oldIds = Array.from(oldVariantIds).sort().join(',');
    const newIds = Array.from(newVariantIds).sort().join(',');

    const hasChanges = oldIds !== newIds;

    return hasChanges;
  }

  detectNewEntitlements(oldEntitlements, newEntitlements) {
    if (!Array.isArray(oldEntitlements) || oldEntitlements.length === 0) {
      return [];
    }

    if (!Array.isArray(newEntitlements) || newEntitlements.length === 0) {
      return [];
    }

    const oldVariantIds = new Set(oldEntitlements.map(e => String(e.variantProductId)));
    const newVariantIds = newEntitlements.map(e => String(e.variantProductId));

    const addedVariants = newVariantIds.filter(variantId => !oldVariantIds.has(variantId));

    if (addedVariants.length > 0) {
      return addedVariants;
    }

    return [];
  }

  invalidate() {
    this.storage.removeItem(this.CACHE_KEY);
    this.invalidateProductValidationCache();
  }

  invalidateProductValidationCache() {
    this.storage.removeItem(this.PRODUCTS_CACHE_KEY);
    this.sessionStorage.removeItem(this.SESSION_CACHE_KEY);
  }

  cacheProductValidation(variantId, validationData) {
    try {
      const currentCustomerId = window.theme?.customer?.id;

      if (currentCustomerId) {
        const productsCache = this.getProductsCache();

        const cacheData = {
          customerId: currentCustomerId,
          products: {
            ...productsCache,
            [variantId]: {
              timestamp: Date.now(),
              data: validationData,
            },
          },
        };

        this.storage.setItem(this.PRODUCTS_CACHE_KEY, JSON.stringify(cacheData));
      }

      const sessionCache = this.getSessionCache();

      sessionCache[variantId] = {
        timestamp: Date.now(),
        data: validationData,
      };

      this.sessionStorage.setItem(this.SESSION_CACHE_KEY, JSON.stringify(sessionCache));
    } catch (error) {
      console.error('[EntitlementsCache] ❌ Error caching product validation:', error);
    }
  }

  getSessionCache() {
    try {
      const cached = this.sessionStorage.getItem(this.SESSION_CACHE_KEY);

      if (!cached) {
        return {};
      }

      return JSON.parse(cached);
    } catch (error) {
      console.error('[EntitlementsCache] ❌ Error getting session cache:', error);

      return {};
    }
  }

  getProductsCache() {
    try {
      const cached = this.storage.getItem(this.PRODUCTS_CACHE_KEY);

      if (!cached) {
        return {};
      }

      const productsCache = JSON.parse(cached);
      const currentCustomerId = window.theme?.customer?.id;

      if (!currentCustomerId) {
        this.storage.removeItem(this.PRODUCTS_CACHE_KEY);

        return {};
      }

      if (productsCache.customerId && Number(productsCache.customerId) !== Number(currentCustomerId)) {
        this.storage.removeItem(this.PRODUCTS_CACHE_KEY);

        return {};
      }

      return productsCache.products || {};
    } catch (error) {
      console.error('[EntitlementsCache] ❌ Error getting products cache:', error);

      return {};
    }
  }

  getCachedValidation(variantId) {
    const sessionCache = this.getSessionCache();
    const sessionCached = sessionCache[variantId];

    if (sessionCached) {
      return sessionCached.data;
    }

    const currentCustomerId = window.theme?.customer?.id;

    if (!currentCustomerId) {
      return null;
    }

    const productsCache = this.getProductsCache();
    const cached = productsCache[variantId];

    if (!cached) {
      return null;
    }

    const age = Date.now() - cached.timestamp;
    const isExpired = age > this.CACHE_TTL;

    if (isExpired) {
      return null;
    }

    return cached.data;
  }

  updateAfterPurchase() {
    this.invalidate();
  }
}

window.EntitlementsCache = EntitlementsCache;

if (typeof window !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      window.entitlementsCache = new EntitlementsCache();
    });
  } else {
    window.entitlementsCache = new EntitlementsCache();
  }
}
